'use client';

import { Button } from "@/components/ui/button";
import { ArrowRight, Home, TrendingUp, Users, Shield, Sparkles, Zap, Wrench } from 'lucide-react';
import { motion } from 'framer-motion';

export default function PropertyOwnerHero() {
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 50, scale: 0.9 },
        visible: { opacity: 1, y: 0, scale: 1 }
    };

    return (
        <section className="py-20 bg-gradient-to-br from-primary/10 to-primary/5 relative overflow-hidden">
            {/* Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -left-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute -top-20 -right-20 w-60 h-60 bg-secondary/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
                <div className="absolute -bottom-40 left-1/4 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse delay-2000"></div>
            </div>

            <div className="container mx-auto px-4 relative z-10">
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="container mx-auto text-center"
                >
                    <motion.div variants={itemVariants} className="mb-8">
                        <div className="inline-flex items-center gap-3 bg-secondary text-white px-4 py-1 rounded-full text-sm font-medium mb-6">
                            <Sparkles className="w-4 h-4 animate-pulse" />
                            <span>Property Owner Solutions</span>
                            <Zap className="w-4 h-4 animate-pulse" />
                        </div>

                        <h2 className="text-3xl lg:text-4xl font-semibold text-gray-800 mb-6">
                            Turn Your Property Into
                            <motion.span
                                className="text-primary block"
                                initial={{ opacity: 0, scale: 0.8 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.8, delay: 0.3 }}
                                viewport={{ once: true }}
                            >
                                Passive Income
                            </motion.span>
                        </h2>
                        <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                            Join thousands of property owners who are earning more with our premium holiday rental platform.
                            We handle everything so you can focus on what matters most.
                        </p>
                    </motion.div>

                    <motion.div
                        variants={containerVariants}
                        className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12"
                    >
                        <motion.div
                            variants={cardVariants}
                            whileHover={{ scale: 1.05, y: -10 }}
                            className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden"
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            <div className="relative z-10">
                                <motion.div
                                    className="w-12 h-12 bg-secondary text-white group-hover:bg-white group-hover:text-secondary group-hover:border group-hover:border-secondary rounded-full flex items-center justify-center mx-auto mb-4"
                                    whileHover={{ rotate: 360 }}
                                    transition={{ duration: 0.6 }}
                                >
                                    <TrendingUp className="w-6 h-6" />
                                </motion.div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Earn 30% More</h3>
                                <p className="text-gray-600 text-sm">
                                    Our platform helps you maximize your rental income with dynamic pricing and premium positioning
                                </p>
                            </div>
                        </motion.div>

                        <motion.div
                            variants={cardVariants}
                            whileHover={{ scale: 1.05, y: -10 }}
                            className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden"
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            <div className="relative z-10">
                                <motion.div
                                    className="w-12 h-12 bg-secondary text-white group-hover:bg-white group-hover:text-secondary group-hover:border group-hover:border-secondary rounded-full flex items-center justify-center mx-auto mb-4"
                                    whileHover={{ rotate: 360 }}
                                    transition={{ duration: 0.6 }}
                                >
                                    <Shield className="w-6 h-6" />
                                </motion.div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Full Protection</h3>
                                <p className="text-gray-600 text-sm">
                                    Comprehensive insurance, guest screening, and 24/7 support to protect your investment
                                </p>
                            </div>
                        </motion.div>

                        <motion.div
                            variants={cardVariants}
                            whileHover={{ scale: 1.05, y: -10 }}
                            className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden"
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            <div className="relative z-10">
                                <motion.div
                                    className="w-12 h-12 bg-secondary text-white group-hover:bg-white group-hover:text-secondary group-hover:border group-hover:border-secondary rounded-full flex items-center justify-center mx-auto mb-4"
                                    whileHover={{ rotate: 360 }}
                                    transition={{ duration: 0.6 }}
                                >
                                    <Users className="w-6 h-6" />
                                </motion.div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Expert Management</h3>
                                <p className="text-gray-600 text-sm">
                                    Professional photography, marketing, and guest management handled by our expert team
                                </p>
                            </div>
                        </motion.div>

                        <motion.div
                            variants={cardVariants}
                            whileHover={{ scale: 1.05, y: -10 }}
                            className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden"
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            <div className="relative z-10">
                                <motion.div
                                    className="w-12 h-12 bg-secondary text-white group-hover:bg-white group-hover:text-secondary group-hover:border group-hover:border-secondary rounded-full flex items-center justify-center mx-auto mb-4"
                                    whileHover={{ rotate: 360 }}
                                    transition={{ duration: 0.6 }}
                                >
                                    <Wrench className="w-6 h-6" />
                                </motion.div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Property Maintenance</h3>
                                <p className="text-gray-600 text-sm">
                                    Skilled technicians handle routine maintenance and repairs at discounted rates; no markups on invoices.
                                </p>
                            </div>
                        </motion.div>
                    </motion.div>

                    {/* <motion.div
                        variants={itemVariants}
                        className="flex flex-col sm:flex-row gap-4 justify-center"
                    >
                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                            <Button variant="outline" size="lg">
                                Learn More
                            </Button>
                        </motion.div>
                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                            <Button size="lg">
                                List Your Property
                                <motion.div
                                    animate={{ x: [0, 5, 0] }}
                                    transition={{ duration: 1.5, repeat: Infinity }}
                                >
                                    <ArrowRight className="ml-2 w-5 h-5" />
                                </motion.div>
                            </Button>
                        </motion.div>
                    </motion.div> */}

                    <motion.div
                        variants={containerVariants}
                        className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-8 text-sm text-gray-500"
                    >
                        <motion.div
                            variants={itemVariants}
                            whileHover={{ scale: 1.1 }}
                            className="flex items-center gap-2 p-3 rounded-lg hover:bg-white/50 transition-colors cursor-pointer"
                        >
                            <motion.div
                                animate={{ rotate: [0, 10, -10, 0] }}
                                transition={{ duration: 2, repeat: Infinity }}
                            >
                                <Home className="w-4 h-4" />
                            </motion.div>
                            <span>500+ Properties Listed</span>
                        </motion.div>
                        <motion.div
                            variants={itemVariants}
                            whileHover={{ scale: 1.1 }}
                            className="flex items-center gap-2 p-3 rounded-lg hover:bg-white/50 transition-colors cursor-pointer"
                        >
                            <motion.div
                                animate={{ y: [0, -5, 0] }}
                                transition={{ duration: 2, repeat: Infinity }}
                            >
                                <TrendingUp className="w-4 h-4" />
                            </motion.div>
                            <span>95% Occupancy Rate</span>
                        </motion.div>
                        <motion.div
                            variants={itemVariants}
                            whileHover={{ scale: 1.1 }}
                            className="flex items-center gap-2 p-3 rounded-lg hover:bg-white/50 transition-colors cursor-pointer"
                        >
                            <motion.div
                                animate={{ scale: [1, 1.2, 1] }}
                                transition={{ duration: 2, repeat: Infinity }}
                            >
                                <Users className="w-4 h-4" />
                            </motion.div>
                            <span>10,000+ Happy Guests</span>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}
